// Finar rev. madde 12 (2026-10-30) — Sistem Yöneticisi (`superadmin`) rolü + Veri Kataloğu
// kategori izni için FE kapı yardımcıları (saf). Sunucu aynı kuralları ayrıca uygular
// (users/roles servisleri 403 `superadmin_required`; katalog route guard'ı 403).

import {
  SUPERADMIN_ROLE_NAME,
  kpiCatalogCategoryIds,
  type KpiCatalogNodeRef,
  type Permissions,
} from "@mahir/shared";

/** Kategori kapısı ipucu (düğme `title` + salt okunur pencere notu). */
export const SUPERADMIN_ONLY_HINT = "Yalnız Sistem Yöneticisi";

/** Rev. madde 12 FE kopyası (tek kaynak — satır içi yazılmaz). */
export const ROLE_UI_COPY = {
  categoryHint: `Kategori: ${SUPERADMIN_ONLY_HINT}`,
  superadminCard: {
    title: "Sistem Yöneticisi",
    description: "Admin yetkilerinin tamamı + Veri Kataloğu kategorilerini yönetir.",
  },
} as const;

/** Yalnız Sistem Yöneticisi'nin verebildiği izin anahtarları (özel rol matrisinde kilitli). */
const SUPERADMIN_ONLY_PERMISSIONS: readonly string[] = ["kpi.catalog.categories"];

/** Rol matrisinde bu izin kutusu aktöre kilitli mi? (sunucu 403 `superadmin_required`) */
export function permissionLockedFor(
  key: string,
  actor: { roleName: string } | null | undefined,
): boolean {
  return SUPERADMIN_ONLY_PERMISSIONS.includes(key) && !isSuperadminUser(actor);
}

export function isSuperadminUser(user: { roleName: string } | null | undefined): boolean {
  return user?.roleName === SUPERADMIN_ROLE_NAME;
}

type PermUser = { permissions: Permissions } | null | undefined;

/**
 * Yönetici panosu (AdminDashboard) — rol ADINA değil izne bakılır (rev. madde 12 review B1:
 * `roleName === "admin"` Sistem Yöneticisi'ni danışman panosuna düşürüyordu). `system.admin`
 * hem `admin` hem `superadmin` rolünde.
 */
export function seesAdminDashboard(user: PermUser): boolean {
  return user?.permissions["system.admin"] === true;
}

/** Review "Projeyi Reopen Et" — sunucu ucu `projects.reopen` ister; FE aynı izne bakar. */
export function canReopenProject(user: PermUser): boolean {
  return user?.permissions["projects.reopen"] === true;
}

export function canManageCatalogCategories(perms: Permissions | null | undefined): boolean {
  return perms?.["kpi.catalog.categories"] === true;
}

/**
 * Rol seçicide gösterilecek roller: `superadmin` yalnız Sistem Yöneticisi'ne seçenek olur;
 * kullanıcının mevcut rolü (superadmin olsa bile) değer olarak görünür kalır.
 */
export function assignableRoles<T extends { id: string; name: string }>(
  roles: readonly T[],
  actor: { roleName: string } | null | undefined,
  currentRoleId?: string,
): T[] {
  const sa = isSuperadminUser(actor);
  return roles.filter((r) => sa || r.name !== SUPERADMIN_ROLE_NAME || r.id === currentRoleId);
}

/** Hedef kullanıcının rol/aktiflik ayarı bu aktörce değiştirilebilir mi? */
export function canChangeUserRole(
  targetRoleName: string,
  actor: { roleName: string } | null | undefined,
): boolean {
  return targetRoleName !== SUPERADMIN_ROLE_NAME || isSuperadminUser(actor);
}

/**
 * Katalog düğümü bu kullanıcıca düzenlenebilir mi? `kpi.manage` şart; kategori düğümü
 * (global kök ya da çocuklu — shared `isKpiCatalogCategory`) ayrıca `kpi.catalog.categories` ister.
 */
export function catalogNodeEditable(
  nodeId: string,
  catalog: readonly KpiCatalogNodeRef[],
  perms: Permissions | null | undefined,
): boolean {
  if (perms?.["kpi.manage"] !== true) return false;
  if (canManageCatalogCategories(perms)) return true;
  return !kpiCatalogCategoryIds(catalog).has(nodeId);
}
